
import ClockChangeDial from '@/components/ClockChangeDial.vue'
import {
  clockChangeBannerFor,
  countdownParts,
  formatChangeDate,
  formatClockHour,
  formatDaysLeft,
  localShiftHours,
} from '@/utils/clockChange'
import { cookieOpts, COOKIE_DURATION } from '@/utils/cookieOptions'
import {
  LIGHTING_GUIDE_ROUTE,
  lightingGuideAvailable,
} from '@/utils/lightingGuide'

const REWIND_SESSION_KEY = 'agm-clock-change-rewind'
const COUNTDOWN_PHASES = ['countdown', 'tonight']

export default {
  name: 'ClockChangeBanner',
  components: {
    ClockChangeDial,
  },
  data() {
    return {
      prefix: 'components.clockChangeBanner',
      liveNow: null,
      mounted: false,
      rewind: false,
      secondOffset: 0,
      localHours: null,
      timer: null,
    }
  },
  computed: {
    now() {
      return this.liveNow ?? this.$store.state.clock_now
    },
    context() {
      return clockChangeBannerFor(
        this.$store.state,
        this.$i18n.locale,
        this.now
      )
    },
    guideLink() {
      return lightingGuideAvailable(this.$i18n.locale)
        ? this.localePath({ name: LIGHTING_GUIDE_ROUTE })
        : null
    },
    hours() {
      return this.localHours || { from: this.context.from, to: this.context.to }
    },
    fromLabel() {
      return formatClockHour(this.$i18n.locale, this.hours.from)
    },
    toLabel() {
      return formatClockHour(this.$i18n.locale, this.hours.to)
    },
    shift() {
      return this.$t(`${this.prefix}.shift`, {
        from: this.fromLabel,
        to: this.toLabel,
      })
    },
    longDate() {
      return formatChangeDate(this.$i18n.locale, this.context.at)
    },
    shortDate() {
      return formatChangeDate(this.$i18n.locale, this.context.at, 'short')
    },
    daysLeft() {
      return formatDaysLeft(this.$i18n.locale, this.context.daysLeft)
    },
    countdown() {
      return countdownParts(this.context.at - this.now).join(':')
    },
    announcement() {
      if (['done', 'after'].includes(this.context.phase)) {
        return this.$t(`${this.prefix}.${this.context.phase}`, {
          season: this.$t(`${this.prefix}.season`),
        })
      }
      return this.$t(`${this.prefix}.announcement`, {
        date: this.longDate,
        shift: this.shift,
      })
    },
    ticksEverySecond() {
      return (
        Boolean(this.context) && COUNTDOWN_PHASES.includes(this.context.phase)
      )
    },
  },
  watch: {
    ticksEverySecond() {
      this.schedule()
    },
  },
  mounted() {
    this.liveNow = this.currentTime()
    this.secondOffset = new Date(this.liveNow).getUTCSeconds()
    this.mounted = true
    if (this.context) {
      this.localHours = localShiftHours(
        this.context.at,
        ms => -new Date(ms).getTimezoneOffset()
      )
      this.rewind = this.firstViewOfSession()
    }
    this.schedule()
    document.addEventListener('visibilitychange', this.schedule)
  },
  beforeDestroy() {
    clearTimeout(this.timer)
    document.removeEventListener('visibilitychange', this.schedule)
  },
  methods: {
    currentTime() {
      return Date.now() + (this.$store.state.clock_offset || 0)
    },
    schedule() {
      clearTimeout(this.timer)
      if (document.visibilityState === 'hidden') return
      this.liveNow = this.currentTime()
      const step = this.ticksEverySecond ? 1000 : 30000
      this.timer = setTimeout(this.schedule, step - (this.liveNow % 1000))
    },
    firstViewOfSession() {
      if (!['before', 'countdown', 'tonight'].includes(this.context.phase)) {
        return false
      }
      try {
        if (window.sessionStorage.getItem(REWIND_SESSION_KEY)) return false
        window.sessionStorage.setItem(REWIND_SESSION_KEY, '1')
        return true
      } catch {
        return false
      }
    },
    dismiss() {
      const key = this.context.key
      this.$nuxtCookies.set(
        'hide_clock_change',
        key,
        cookieOpts(COOKIE_DURATION.MONTH)
      )
      this.$store.commit('SET_CLOCK_CHANGE_HIDDEN', key)
    },
  },
}
